import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, RotateCcw } from "lucide-react";
import { useState } from "react";
import type { Agent } from "@/types/aiToolkit";
import type { AgentHandoffSession } from "@/types/agentHandoff";
import { agentAllowsResume } from "@/lib/agentChatHandoff";
import { updateHandoffSession } from "@/lib/agentHandoffSession";
import { buildChatRefinementsSummary } from "@/lib/agentHandoffMerge";

interface AgentHandoffBannerProps {
  session: AgentHandoffSession;
  agent: Agent | null;
  messages: Array<{ role: string; content: string }>;
  currentThreadId: string | null;
  onDismiss?: () => void;
}

export function AgentHandoffBanner({
  session,
  agent,
  messages,
  currentThreadId,
  onDismiss,
}: AgentHandoffBannerProps) {
  const [, setLocation] = useLocation();
  const [resuming, setResuming] = useState(false);

  if (!agentAllowsResume(agent)) return null;

  const handleResume = async () => {
    setResuming(true);
    try {
      const chatRefinements = buildChatRefinementsSummary(messages);
      updateHandoffSession(session.id, {
        chatRefinements,
        chatThreadId: currentThreadId ?? session.chatThreadId,
      });

      const isAutomatic = agent?.executionMode === "automatic";
      const params = new URLSearchParams({
        resumeHandoff: session.id,
        ...(isAutomatic ? { autoRerun: "1" } : {}),
      });

      setLocation(`${session.returnPath}?${params.toString()}`);
      onDismiss?.();
    } finally {
      setResuming(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <Badge variant="secondary" className="shrink-0">
          Agent handoff
        </Badge>
        <span className="text-muted-foreground truncate">
          Discussing <strong className="text-foreground">{session.agentName}</strong> results from{" "}
          {session.source} ({session.items.length} item{session.items.length !== 1 ? "s" : ""})
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button
          size="sm"
          variant="outline"
          disabled={resuming}
          onClick={() => setLocation(session.returnPath)}
        >
          <ArrowLeft className="h-3.5 w-3.5 mr-1" />
          Back
        </Button>
        <Button size="sm" disabled={resuming} onClick={() => void handleResume()}>
          {resuming ? (
            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
          ) : (
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
          )}
          Resume agent work
        </Button>
      </div>
    </div>
  );
}
